/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type Maybe from 'graphql/tsutils/Maybe';

import Imgix from 'js/components/Imgix';

import type { Theme } from '@coursera/cds-core';
import { Grid, Link, Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import type { CtaFragment, ImageFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

const styles = {
  cnt: css`
    margin: var(--cds-spacing-300) 0;
    border-radius: var(--cds-spacing-100);
    border: 1px solid #dedede;
    background: var(--cds-color-white-0);
    overflow: hidden;
    padding: var(--cds-spacing-50);
    cursor: pointer;
    text-decoration: none !important;

    :hover {
      a {
        text-decoration: underline;
      }
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-100);
    }
  `,
  image: (_: Theme, type?: Maybe<string>) => css`
    max-width: unset !important;
    width: ${type === 'inline' ? 'unset' : '100%'};
    min-height: 100%;
    height: 100px;
    object-fit: cover;
    aspect-ratio: 16 / 9;
    border-radius: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      height: 120px;
    }

    ${breakpoints.down('xs')} {
      height: unset;
    }
  `,
  headline: css`
    font-size: 20px;
    font-style: normal;
    letter-spacing: -0.6px;
    margin-bottom: var(--cds-spacing-100);
  `,
  body: (_: Theme, type?: Maybe<string>) => css`
    font-size: 16px;
    font-style: normal;
    line-height: 1;
    letter-spacing: -0.48px;
    margin-bottom: ${type === 'inline' ? '0' : ' var(--cds-spacing-150)'};
    flex: 1;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    margin-bottom: var(--cds-spacing-150);

    * {
      color: var(--cds-color-neutral-primary) !important;
    }

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  bodyCnt: (_: Theme, type?: Maybe<string>) => css`
    padding: ${type === 'inline'
      ? ' var(--cds-spacing-100)  var(--cds-spacing-150)  var(--cds-spacing-100) var(--cds-spacing-150)'
      : ' var(--cds-spacing-150) 0 0 0'};
    display: flex;
    flex-direction: column;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-150) 0 0 0;
    }
  `,
  cta: css`
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    letter-spacing: -0.28px;
    text-decoration: none;
  `,
};

export type Props = {
  alignment?: Maybe<string>;
  headline?: Maybe<string>;
  body?: Maybe<string>;
  cta?: Maybe<CtaFragment>;
  image?: Maybe<ImageFragment>;
  module: BlockLevelModule;
};

const getPromoUnitStylesForType = (type?: Maybe<string>) => {
  const sharedFullWidthButtonStyle = css`
    a {
      width: 100%;
      max-width: 100%;
    }
  `;

  const inlineCss = css`
    width: 100%;
    flex-direction: row !important;
    flex-wrap: nowrap;

    ${breakpoints.down('xs')} {
      flex-direction: column !important;
      ${sharedFullWidthButtonStyle}
    }
  `;

  const sharedFloatStyles = css`
    width: 320px;
    padding: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      width: 100%;
      margin: var(--cds-spacing-300) 0;
    }
  `;

  switch (type) {
    case 'inline':
      return inlineCss;
    case 'float-left':
      return css`
        float: left;
        margin: var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-300) 0;

        ${sharedFullWidthButtonStyle}
        ${sharedFloatStyles}
      `;
    case 'float-right':
      return css`
        float: right;
        margin: var(--cds-spacing-300) 0 var(--cds-spacing-300) var(--cds-spacing-300);

        ${sharedFullWidthButtonStyle}
        ${sharedFloatStyles}
      `;
    default:
      return inlineCss;
  }
};

const EnterpriseArticleNativeAd = ({ alignment, headline, body, cta, image, module }: Props) => {
  const theme = useTheme();

  if (!cta?.ctaTarget || !cta?.ctaText) return null;

  return (
    <Grid
      container
      direction="column"
      css={[styles.cnt, getPromoUnitStylesForType(alignment)]}
      component="a"
      href={cta?.ctaTarget}
      target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
    >
      <div>
        {image?.url && (
          <Imgix
            src={image?.url}
            alt={image?.description ?? _t('Native Ad Image')}
            css={styles.image(theme, alignment)}
          />
        )}
      </div>
      <Grid direction="column" css={styles.bodyCnt(theme, alignment)}>
        <Typography2 component="h3" variant="subtitleMedium" css={styles.headline}>
          {headline}
        </Typography2>
        <Markdown css={styles.body(theme, alignment)} source={body} module={module} />
        {cta?.ctaTarget && cta?.ctaText && (
          <Link
            component={TrackedA}
            href={cta?.ctaTarget}
            css={styles.cta}
            target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
            trackingName="enterpise_article_native_ad_link"
          >
            {cta?.ctaText}
          </Link>
        )}
      </Grid>
    </Grid>
  );
};

export default EnterpriseArticleNativeAd;
